import React, { useState } from 'react';
import { Camera, ImageIcon, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AiBadge } from './PhotoUpload';
import { formatDate } from './constants';
import { authFetch, API } from '../../utils/api';


export default function PhotoGallery({ photos, boardId }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  if (!photos || photos.length === 0) return null;

  const loadFullPhoto = async (photoId) => {
    setLoadingPhoto(true);
    try {
      const res = await authFetch(`${API}/api/boards/${boardId}/photos/${photoId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedPhoto(data);
      }
    } catch {} finally { setLoadingPhoto(false); }
  };

  return (
    <div data-testid="photo-gallery">
      <div className="flex items-center gap-1.5 mb-2">
        <ImageIcon className="w-3 h-3 text-secondary/50" />
        <span className="text-[10px] text-muted-foreground/60">{photos.length} photo{photos.length !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {photos.map((p) => (
          <button
            key={p.photo_id}
            onClick={() => loadFullPhoto(p.photo_id)}
            className="flex-shrink-0 w-16 h-16 rounded-lg border border-white/10 bg-white/5 flex flex-col items-center justify-center hover:border-primary/30 transition-colors overflow-hidden"
            data-testid={`photo-thumb-${p.photo_id}`}
          >
            <Camera className="w-4 h-4 text-muted-foreground/30" />
            <span className={`text-[7px] mt-0.5 font-bold ${p.ai_assessment?.condition === 'New' ? 'text-green-400' : p.ai_assessment?.condition === 'Good' ? 'text-primary' : p.ai_assessment?.condition === 'Fair' ? 'text-yellow-400' : p.ai_assessment?.condition === 'Thrashed' ? 'text-red-400' : 'text-muted-foreground/40'}`}>
              {p.ai_assessment?.condition || '?'}
            </span>
          </button>
        ))}
      </div>

      {/* Full photo modal */}
      <AnimatePresence>
        {(selectedPhoto || loadingPhoto) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => { setSelectedPhoto(null); setLoadingPhoto(false); }}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={e => e.stopPropagation()}
              className="w-full max-w-sm bg-bg-paper border border-white/10 rounded-2xl overflow-hidden shadow-2xl"
              data-testid="photo-modal"
            >
              {loadingPhoto ? (
                <div className="h-48 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : selectedPhoto ? (
                <>
                  <img
                    src={`data:${selectedPhoto.mime_type || 'image/webp'};base64,${selectedPhoto.image}`}
                    alt="Board condition"
                    className="w-full max-h-64 object-contain bg-black"
                    data-testid="photo-full-view"
                  />
                  <div className="p-4 space-y-2">
                    {selectedPhoto.ai_assessment && <AiBadge assessment={selectedPhoto.ai_assessment} />}
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] text-muted-foreground/40">
                        by {selectedPhoto.uploaded_by_name || '?'} · {formatDate(selectedPhoto.uploaded_at)}
                      </p>
                      <button onClick={() => setSelectedPhoto(null)} className="text-[10px] text-muted-foreground hover:text-white px-2 py-1 rounded-lg hover:bg-white/10">Close</button>
                    </div>
                  </div>
                </>
              ) : null}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
